import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, Crown, Trophy, Users, Coins } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { dateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, StatusBadge } from "@/components/brand";
import { TeamBadge } from "@/components/SportsCards";
import { Skeleton } from "@/components/ui/skeleton";

const ROLES = ["WK", "BAT", "AR", "BOWL"];
const prizeText = (c) => (c.prize_type === "top3" ? "Top 3 win · 50/30/20%" : "Winner takes all");

const Banner = ({ m }) => (
  <div className="relative overflow-hidden rounded-2xl border border-cr-gold/30 p-6">
    <img src="/brand/cricket/stumps.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
    <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
    <div className="relative flex items-center gap-4">
      <TeamBadge team={m.home} big /><p className="font-display text-xl font-black text-cr-muted">vs</p><TeamBadge team={m.away} big />
      <div className="ml-2"><p className="font-display text-lg font-bold sm:text-2xl">{m.home.name} vs {m.away.name}</p><p className="text-xs text-cr-ink/75">{m.league} · {dateTime(m.start)}</p></div>
    </div>
  </div>
);

export default function Fantasy() {
  const [rows, setRows] = useState(null);
  const [mine, setMine] = useState([]);
  const { user } = useAuth();
  useEffect(() => { api.get("/fantasy/matches").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  useEffect(() => { if (user?.role === "user") { api.get("/fantasy/my-contests").then((r) => setMine(r.data)).catch(() => {}); } }, [user]);
  return (
    <main data-testid="fantasy-page" className="mx-auto max-w-7xl px-5 py-10">
      <div className="relative mb-8 overflow-hidden rounded-2xl border border-cr-gold/30">
        <img src="/brand/cricket/trophy.jpg" alt="Trophy" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/20" />
        <div className="relative max-w-xl p-8 sm:p-12">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-cr-gold">Fantasy cricket</p>
          <h1 className="mt-3 text-4xl font-black uppercase sm:text-5xl lg:text-6xl">Build your <span className="gold-text">dream XI</span></h1>
          <p className="mt-3 text-sm text-cr-ink/85 sm:text-base">Pick 11 players, choose a captain (2x) and vice-captain (1.5x), join contests with coins and climb the live leaderboard.</p>
        </div>
      </div>
      {mine.length > 0 && <section className="mb-10"><h2 className="mb-3 text-lg font-bold">My contests</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{mine.map((c) => (
          <Link key={c.contest_id} to={`/fantasy/contest/${c.contest_id}`} data-testid={`my-contest-${c.contest_id}`} className="card-cr p-4 hover:border-cr-gold/50">
            <div className="flex justify-between text-xs text-cr-muted"><span>{c.match}</span><StatusBadge status={c.status} /></div>
            <p className="mt-2 font-semibold">{c.name} · {c.team_name}</p>
            <p className="mt-1 text-sm text-cr-gold">{c.rank ? `Rank #${c.rank} · ${c.prize} coins won` : `Entry ${c.entry_fee} coins`}</p>
          </Link>))}</div></section>}
      <h2 className="mb-3 text-lg font-bold">Upcoming matches</h2>
      {rows === null ? <Skeleton className="h-40 bg-cr-elevated" /> : rows.length === 0 ? <EmptyState title="No matches open" text="New fantasy matches appear as soon as teams are confirmed." /> :
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{rows.map((m) => (
          <Link key={m.id} to={`/fantasy/${m.id}`} data-testid={`fantasy-match-${m.id}`} className="card-cr group p-5 transition-colors hover:border-cr-gold/50">
            <p className="text-xs text-cr-muted">{m.league} · {dateTime(m.start)}</p>
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-2"><TeamBadge team={m.home} /><span className="font-semibold">{m.home.short}</span></div>
              <span className="text-xs font-bold text-cr-muted">VS</span>
              <div className="flex items-center gap-2"><span className="font-semibold">{m.away.short}</span><TeamBadge team={m.away} /></div>
            </div>
            <span className="mt-4 inline-flex w-full justify-center rounded-lg bg-cr-gold py-2 text-sm font-bold text-black">Create team & join</span>
          </Link>))}</div>}
    </main>
  );
}

const JoinBox = ({ c, teams, onJoined }) => {
  const [team, setTeam] = useState(teams[0]?.id || "");
  const [busy, setBusy] = useState(false);
  const { refreshWallet } = useAuth();
  const join = async () => {
    setBusy(true);
    try { await api.post(`/fantasy/contests/${c.id}/join`, { team_id: team }); toast.success("Joined contest"); refreshWallet(); onJoined(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  if (c.joined_team) return <Link to={`/fantasy/contest/${c.id}`} className="btn-outline px-4 py-2 text-xs normal-case">Joined · view</Link>;
  if (!teams.length) return <span className="text-xs text-cr-muted">Create a team first</span>;
  return (
    <div className="flex items-center gap-2">
      <select data-testid={`join-team-${c.id}`} value={team} onChange={(e) => setTeam(e.target.value)} className="input-cr w-20 py-2 text-xs">{teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select>
      <button data-testid={`join-contest-${c.id}`} disabled={busy || c.entries >= c.max_entries} onClick={join} className="btn-gold px-4 py-2 text-xs normal-case">{c.entries >= c.max_entries ? "Full" : `Join · ${c.entry_fee}`}</button>
    </div>
  );
};

export function FantasyMatch() {
  const { fid } = useParams();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const load = () => api.get(`/fantasy/matches/${fid}`).then((r) => setD(r.data)).catch((e) => { toast.error(apiError(e)); setD(false); });
  useEffect(() => { if (user) load(); }, [fid, user]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!user) return <main className="mx-auto max-w-3xl px-5 py-16"><EmptyState title="Log in to play fantasy" action={<Link to="/login" className="btn-gold">Log in</Link>} /></main>;
  if (d === false) return <main className="mx-auto max-w-3xl px-5 py-16"><EmptyState title="Match not available" action={<Link to="/fantasy" className="btn-gold">Back</Link>} /></main>;
  if (!d) return <main className="mx-auto max-w-5xl px-5 py-10"><Skeleton className="h-64 bg-cr-elevated" /></main>;
  return (
    <main data-testid="fantasy-match-page" className="mx-auto max-w-5xl px-5 py-10">
      <Link to="/fantasy" className="mb-5 inline-flex items-center gap-2 text-sm text-cr-gold"><ArrowLeft className="h-4 w-4" />All matches</Link>
      <Banner m={d.match} />
      <div className="mt-8 flex items-center justify-between"><h2 className="text-lg font-bold">My teams ({d.teams.length}/6)</h2>
        {d.teams.length < 6 && d.match.can_predict && <Link to={`/fantasy/${fid}/create`} data-testid="create-team-btn" className="btn-gold px-4 py-2 text-sm normal-case">+ Create team</Link>}</div>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">{d.teams.map((t) => (
        <Link key={t.id} to={`/fantasy/${fid}/create?edit=${t.id}`} data-testid={`my-team-${t.name}`} className="card-cr p-4 hover:border-cr-gold/50"><p className="font-display font-bold text-cr-gold">{t.name}</p><p className="text-xs text-cr-muted">11 players · tap to edit</p></Link>))}
        {d.teams.length === 0 && <p className="text-sm text-cr-muted">No teams yet. Create one to join contests.</p>}</div>
      <h2 className="mb-3 mt-10 text-lg font-bold">Contests</h2>
      <div className="space-y-3">{d.contests.map((c) => (
        <div key={c.id} data-testid={`contest-${c.id}`} className="card-cr flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="flex items-center gap-2 font-semibold"><Trophy className="h-4 w-4 text-cr-gold" />{c.name}{c.auto ? "" : <span className="rounded bg-cr-gold/15 px-2 text-[10px] text-cr-gold">Special</span>}</p>
            <p className="mt-1 text-sm">Prize pool up to <b className="text-cr-gold">{c.max_prize_pool.toLocaleString("en-IN")} coins</b> · {prizeText(c)}</p>
            <div className="mt-2 h-1.5 w-56 overflow-hidden rounded bg-cr-elevated"><div className="h-full bg-cr-gold" style={{ width: `${(c.entries / c.max_entries) * 100}%` }} /></div>
            <p className="mt-1 flex items-center gap-1 text-xs text-cr-muted"><Users className="h-3 w-3" />{c.entries}/{c.max_entries} joined</p>
          </div>
          <JoinBox c={c} teams={d.teams} onJoined={load} />
        </div>))}</div>
    </main>
  );
}

const PlayerRow = ({ p, on, cap, vc, toggle, setCap, setVc, step }) => (
  <div data-testid={`player-${p.id}`} className={`flex items-center gap-3 rounded-lg border p-3 transition-colors ${on ? "border-cr-gold bg-cr-gold/5" : "border-cr-line"}`}>
    <img src={p.flag || "/flags/wi.svg"} alt="" className="h-8 w-8 rounded-full object-cover" />
    <div className="min-w-0 flex-1"><p className="truncate font-semibold">{p.name}</p><p className="text-xs text-cr-muted">{p.team} · {p.role}</p></div>
    {step === 1 ? (
      <button data-testid={`pick-${p.id}`} onClick={() => toggle(p)} className={`grid h-8 w-8 place-items-center rounded-full border ${on ? "border-cr-gold bg-cr-gold text-black" : "border-cr-line"}`}>{on ? <Check className="h-4 w-4" /> : "+"}</button>
    ) : (
      <div className="flex gap-2">
        <button data-testid={`cap-${p.id}`} onClick={() => setCap(p.id)} className={`h-8 w-8 rounded-full border text-xs font-bold ${cap === p.id ? "border-cr-gold bg-cr-gold text-black" : "border-cr-line"}`}>C</button>
        <button data-testid={`vc-${p.id}`} onClick={() => setVc(p.id)} className={`h-8 w-8 rounded-full border text-xs font-bold ${vc === p.id ? "border-sky-400 bg-sky-400 text-black" : "border-cr-line"}`}>VC</button>
      </div>)}
  </div>
);

export function TeamBuilder() {
  const { fid } = useParams();
  const [sp] = useSearchParams();
  const editId = sp.get("edit");
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [sel, setSel] = useState([]);
  const [cap, setCap] = useState(null);
  const [vc, setVc] = useState(null);
  const [role, setRole] = useState("ALL");
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/fantasy/matches/${fid}/squad`).then((r) => setS(r.data)); }, [fid]);
  useEffect(() => { if (editId) { api.get(`/fantasy/teams/${editId}`).then((r) => { setSel(r.data.players); setCap(r.data.captain); setVc(r.data.vice_captain); }); } }, [editId]);
  const byId = useMemo(() => Object.fromEntries((s?.players || []).map((p) => [p.id, p])), [s]);
  const counts = (k, v) => sel.filter((id) => byId[id]?.[k] === v).length;
  const toggle = (p) => {
    if (sel.includes(p.id)) return setSel(sel.filter((x) => x !== p.id));
    if (sel.length >= 11) return toast.error("You already have 11 players");
    if (counts("team_side", p.team_side) >= 7) return toast.error("Maximum 7 players from one team");
    setSel([...sel, p.id]);
  };
  const save = async () => {
    setBusy(true);
    const body = { players: sel, captain: cap, vice_captain: vc };
    try { editId ? await api.put(`/fantasy/teams/${editId}`, body) : await api.post(`/fantasy/matches/${fid}/teams`, body); toast.success("Team saved"); nav(`/fantasy/${fid}`); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  if (!s) return <main className="mx-auto max-w-5xl px-5 py-10"><Skeleton className="h-96 bg-cr-elevated" /></main>;
  const list = (step === 1 ? s.players : sel.map((id) => byId[id]).filter(Boolean)).filter((p) => step === 2 || role === "ALL" || p.role === role);
  return (
    <main data-testid="team-builder" className="mx-auto max-w-5xl px-5 py-10 pb-32">
      <Link to={`/fantasy/${fid}`} className="mb-5 inline-flex items-center gap-2 text-sm text-cr-gold"><ArrowLeft className="h-4 w-4" />Back to contests</Link>
      <Banner m={s.match} />
      <div className="sticky top-20 z-30 mt-6 grid grid-cols-3 gap-3 rounded-xl border border-cr-line bg-cr-bg/95 p-4 text-center backdrop-blur sm:grid-cols-6">
        <div><p className="text-xs text-cr-muted">Players</p><p data-testid="picked-count" className="font-display text-xl font-black text-cr-gold">{sel.length}/11</p></div>
        <div><p className="text-xs text-cr-muted">{s.match.home.short}</p><p className="font-display text-xl font-bold">{counts("team_side", "home")}</p></div>
        <div><p className="text-xs text-cr-muted">{s.match.away.short}</p><p className="font-display text-xl font-bold">{counts("team_side", "away")}</p></div>
        {["BAT", "AR", "BOWL"].map((r) => <div key={r} className="hidden sm:block"><p className="text-xs text-cr-muted">{r}</p><p className="font-display text-xl font-bold">{counts("role", r)}</p></div>)}
      </div>
      {step === 1 ? (
        <div className="my-5 flex gap-2">{["ALL", ...ROLES].map((r) => <button key={r} data-testid={`role-filter-${r}`} onClick={() => setRole(r)} className={`rounded-full border px-4 py-1.5 text-sm font-semibold ${role === r ? "border-cr-gold bg-cr-gold text-black" : "border-cr-line"}`}>{r}{r !== "ALL" && ` (${counts("role", r)})`}</button>)}</div>
      ) : <p className="my-5 flex items-center gap-2 text-sm"><Crown className="h-4 w-4 text-cr-gold" />Captain gets 2x points, vice-captain 1.5x.</p>}
      <div className="grid gap-2 sm:grid-cols-2">{list.map((p) => <PlayerRow key={p.id} p={p} on={sel.includes(p.id)} cap={cap} vc={vc} toggle={toggle} setCap={(id) => { setCap(id); if (vc === id) setVc(null); }} setVc={(id) => { setVc(id); if (cap === id) setCap(null); }} step={step} />)}</div>
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-cr-line bg-cr-bg/95 p-4 backdrop-blur">
        <div className="mx-auto flex max-w-5xl gap-3">
          {step === 2 && <button data-testid="builder-back" onClick={() => setStep(1)} className="btn-outline flex-1 py-3 normal-case">Edit players</button>}
          {step === 1 ? <button data-testid="builder-next" disabled={sel.length !== 11} onClick={() => setStep(2)} className="btn-gold flex-1 py-3 normal-case">Next · choose captain</button>
            : <button data-testid="builder-save" disabled={busy || !cap || !vc} onClick={save} className="btn-gold flex-1 py-3 normal-case">{busy ? "Saving…" : "Save team"}</button>}
        </div>
      </div>
    </main>
  );
}

export function ContestView() {
  const { cid } = useParams();
  const [d, setD] = useState(null);
  const [team, setTeam] = useState(null);
  useEffect(() => {
    const load = () => api.get(`/fantasy/contests/${cid}`).then((r) => setD(r.data)).catch(() => setD(false));
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [cid]);
  const open = (tid) => tid && api.get(`/fantasy/teams/${tid}`).then((r) => setTeam(r.data)).catch((e) => toast.error(apiError(e)));
  if (d === false) return <main className="mx-auto max-w-3xl px-5 py-16"><EmptyState title="Contest not found" /></main>;
  if (!d) return <main className="mx-auto max-w-4xl px-5 py-10"><Skeleton className="h-64 bg-cr-elevated" /></main>;
  const c = d.contest;
  return (
    <main data-testid="contest-page" className="mx-auto max-w-4xl px-5 py-10">
      <Link to={`/fantasy/${c.fixture_id}`} className="mb-5 inline-flex items-center gap-2 text-sm text-cr-gold"><ArrowLeft className="h-4 w-4" />{c.match}</Link>
      <div className="card-gold p-6">
        <div className="flex flex-wrap items-center justify-between gap-3"><h1 className="text-2xl font-black">{c.name}</h1><StatusBadge status={c.status} testid="contest-status" /></div>
        <p className="mt-2 text-sm">Prize pool <b className="text-cr-gold">{(d.started ? c.prize_pool : c.max_prize_pool).toLocaleString("en-IN")} coins</b> · {prizeText(c)} · Entry {c.entry_fee} · {c.entries}/{c.max_entries} teams</p>
        {!d.started && <p className="mt-2 text-xs text-cr-muted">Leaderboard and points go live when the match starts.</p>}
      </div>
      <div className="card-cr mt-6 overflow-x-auto">
        <table data-testid="leaderboard" className="w-full min-w-[480px] text-sm">
          <thead className="text-left text-xs uppercase text-cr-muted"><tr>{["Rank", "Player", "Team", "Points", "Won"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
          <tbody>{d.leaderboard.map((r, i) => (
            <tr key={i} onClick={() => open(r.team_id)} className={`cursor-pointer border-t border-cr-line hover:bg-cr-elevated ${r.mine ? "bg-cr-gold/5" : ""}`}>
              <td className="px-4 py-3 font-display font-bold text-cr-gold">#{r.rank}</td><td className="px-4 py-3">{r.user_name}{r.mine && " (you)"}</td>
              <td className="px-4 py-3">{r.team_name}</td><td className="px-4 py-3 font-semibold">{r.points}</td>
              <td className="px-4 py-3">{r.prize ? <span className="flex items-center gap-1 text-cr-gold"><Coins className="h-3 w-3" />{r.prize}</span> : "-"}</td>
            </tr>))}</tbody>
        </table>
      </div>
      {team && (
        <div data-testid="team-preview" className="card-cr mt-6 p-5">
          <div className="mb-3 flex justify-between"><p className="font-display font-bold">{team.user_name} · {team.name}</p><p className="font-bold text-cr-gold">{team.total} pts</p></div>
          <div className="grid gap-2 sm:grid-cols-2">{team.detail.map((p) => (
            <div key={p.id} className="flex items-center justify-between rounded border border-cr-line px-3 py-2 text-sm">
              <span>{p.name} <span className="text-xs text-cr-muted">{p.role}</span>{p.id === team.captain && <b className="ml-1 text-cr-gold">C</b>}{p.id === team.vice_captain && <b className="ml-1 text-sky-400">VC</b>}</span><span>{p.points}</span>
            </div>))}</div>
        </div>)}
      <div className="card-cr mt-6 p-5 text-xs text-cr-muted">
        <p className="mb-2 font-semibold text-cr-ink">Points system</p>
        Run +1 · Four +1 bonus · Six +2 bonus · 30 runs +4 · 50 runs +8 · 100 runs +16 · Duck −2 · Wicket +25 · 3/4/5 wickets +4/+8/+16 · Maiden +12 · Catch/stumping +8 · Run out +6 · Captain 2x · Vice-captain 1.5x
      </div>
    </main>
  );
}
